import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, ChevronLeft, ChevronRight, ExternalLink, Lock, Video } from 'lucide-react';
import { ApiError } from '../lib/api';
import {
  decide,
  getLobby,
  listPublicPosts,
  unlock,
  type PublicPost,
} from '../lib/clientCalendarPublicApi';
import {
  APPROVAL_TONES,
  FORMAT_LABELS,
  PLATFORM_COLORS,
  PLATFORM_LABELS,
  type ApprovalStatus,
} from '../lib/socialPostsApi';

// What the client reads: "La client" is the agency's word, not theirs.
const CLIENT_APPROVAL_LABELS: Record<ApprovalStatus, string> = {
  DRAFT: 'Ciornă',
  PENDING_CLIENT: 'Așteaptă aprobarea ta',
  APPROVED: 'Aprobată',
  CHANGES_REQUESTED: 'Ai cerut modificări',
};

function storageGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function storageSet(key: string, value: string | null) {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // Private mode: the PIN is simply asked again next time.
  }
}
function nameGet(): string {
  try {
    return localStorage.getItem('firmio:client-calendar-name') ?? '';
  } catch {
    return '';
  }
}
function nameSet(value: string) {
  try {
    localStorage.setItem('firmio:client-calendar-name', value);
  } catch {
    // Convenience only.
  }
}

/**
 * /c/:token — the client's view of its content calendar. No Firmio account:
 * the link plus a PIN the agency sent separately. Shows what gets published
 * month by month and lets the client approve a post or ask for changes.
 */
export function PublicClientCalendarPage() {
  const { token = '' } = useParams<{ token: string }>();
  const sessionKey = `firmio:client-calendar:${token}`;
  const [clientName, setClientName] = useState<string | null>(null);
  const [lobbyError, setLobbyError] = useState<string | null>(null);
  const [session, setSession] = useState<string | null>(() => storageGet(sessionKey));

  useEffect(() => {
    getLobby(token)
      .then((l) => setClientName(l.clientName))
      .catch((e) => setLobbyError(e instanceof Error ? e.message : 'Linkul nu este valid.'));
  }, [token]);

  useEffect(() => {
    document.title = clientName ? `Calendar · ${clientName}` : 'Calendar de postări';
  }, [clientName]);

  if (lobbyError) {
    return (
      <Shell>
        <p className="text-center text-sm text-gray-600">{lobbyError}</p>
      </Shell>
    );
  }
  if (!clientName) {
    return (
      <Shell>
        <p className="text-center text-sm text-gray-400">Se încarcă…</p>
      </Shell>
    );
  }
  if (!session) {
    return (
      <PinScreen
        token={token}
        clientName={clientName}
        onUnlocked={(s) => {
          storageSet(sessionKey, s);
          setSession(s);
        }}
      />
    );
  }
  return (
    <Calendar
      token={token}
      clientName={clientName}
      session={session}
      onExpired={() => {
        storageSet(sessionKey, null);
        setSession(null);
      }}
    />
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="mx-auto max-w-sm">{children}</div>
    </div>
  );
}

function PinScreen({
  token,
  clientName,
  onUnlocked,
}: {
  token: string;
  clientName: string;
  onUnlocked: (session: string) => void;
}) {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onUnlocked((await unlock(token, pin)).session);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'PIN greșit.');
      setPin('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      <form
        onSubmit={(e) => void submit(e)}
        className="rounded-xl border border-gray-200 bg-card p-6 text-center shadow-sm"
      >
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-brand-600/15 text-brand-600">
          <Lock size={20} />
        </div>
        <h1 className="mt-3 text-lg font-semibold text-gray-900">Calendarul {clientName}</h1>
        <p className="mt-1 text-sm text-gray-500">Introdu PIN-ul primit de la agenție.</p>
        <input
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          placeholder="••••••"
          aria-label="PIN"
          className="mt-4 w-full rounded-md border border-gray-300 bg-card px-3 py-2.5 text-center text-xl tracking-[0.5em] text-gray-900 focus:border-brand-500 focus:outline-none"
        />
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={pin.length !== 6 || busy}
          className="mt-4 w-full rounded-md bg-brand-600 py-2.5 text-sm font-medium text-fixed-white hover:bg-brand-700 disabled:opacity-50"
        >
          {busy ? 'Se verifică…' : 'Deschide calendarul'}
        </button>
      </form>
    </Shell>
  );
}

function monthRange(year: number, month: number): { from: string; to: string } {
  return {
    from: new Date(year, month, 1).toISOString(),
    to: new Date(year, month + 1, 1).toISOString(),
  };
}

function Calendar({
  token,
  clientName,
  session,
  onExpired,
}: {
  token: string;
  clientName: string;
  session: string;
  onExpired: () => void;
}) {
  const now = new Date();
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [posts, setPosts] = useState<PublicPost[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { from, to } = monthRange(ym.year, ym.month);
    try {
      setError(null);
      setPosts((await listPublicPosts(token, session, from, to)).posts);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) onExpired();
      else setError(e instanceof Error ? e.message : 'Postările nu au putut fi încărcate.');
    }
  }, [token, session, ym, onExpired]);

  useEffect(() => {
    setPosts(null);
    void load();
  }, [load]);

  const byDay = useMemo(() => {
    const map = new Map<string, PublicPost[]>();
    for (const p of posts ?? []) {
      const d = new Date(p.publishAt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      map.set(key, [...(map.get(key) ?? []), p]);
    }
    return [...map.values()];
  }, [posts]);
  const pending = (posts ?? []).filter((p) => p.approvalStatus === 'PENDING_CLIENT').length;

  function shift(direction: 1 | -1) {
    setYm((m) => {
      const d = new Date(m.year, m.month + direction, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  }

  const label = new Date(ym.year, ym.month, 1).toLocaleDateString('ro-RO', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-card">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3 px-4 py-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400">Calendar de postări</p>
            <h1 className="text-lg font-semibold text-gray-900">{clientName}</h1>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => shift(-1)}
              aria-label="Luna trecută"
              className="rounded p-2 text-gray-500 hover:bg-gray-100"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="min-w-32 text-center text-sm font-medium capitalize text-gray-900">
              {label}
            </span>
            <button
              type="button"
              onClick={() => shift(1)}
              aria-label="Luna viitoare"
              className="rounded p-2 text-gray-500 hover:bg-gray-100"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-5 px-4 py-5">
        {pending > 0 && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {pending === 1
              ? 'O postare așteaptă aprobarea ta luna asta.'
              : `${pending} postări așteaptă aprobarea ta luna asta.`}
          </p>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {posts === null && !error && <p className="text-sm text-gray-400">Se încarcă…</p>}
        {posts !== null && posts.length === 0 && (
          <p className="py-10 text-center text-sm text-gray-500">
            Nicio postare programată în luna asta.
          </p>
        )}
        {byDay.map((day) => (
          <section key={day[0].publishAt}>
            <h2 className="mb-2 text-sm font-medium capitalize text-gray-500">
              {new Date(day[0].publishAt).toLocaleDateString('ro-RO', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })}
            </h2>
            <div className="space-y-3">
              {day.map((p) => (
                <PostCard
                  key={p.id}
                  post={p}
                  token={token}
                  session={session}
                  onChanged={(next) =>
                    setPosts((list) => (list ?? []).map((x) => (x.id === next.id ? next : x)))
                  }
                  onExpired={onExpired}
                />
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}

function PostCard({
  post,
  token,
  session,
  onChanged,
  onExpired,
}: {
  post: PublicPost;
  token: string;
  session: string;
  onChanged: (post: PublicPost) => void;
  onExpired: () => void;
}) {
  const [mode, setMode] = useState<'idle' | 'approve' | 'changes'>('idle');
  const [name, setName] = useState(nameGet);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    if (!name.trim()) return setError('Spune-ne cum te numești.');
    if (mode === 'changes' && !comment.trim()) return setError('Spune-ne ce vrei să schimbăm.');
    setBusy(true);
    setError(null);
    try {
      nameSet(name.trim());
      const next = await decide(
        token,
        session,
        post.id,
        mode === 'approve' ? 'approve' : 'request-changes',
        { name: name.trim(), comment: comment.trim() || undefined },
      );
      onChanged(next);
      setMode('idle');
      setComment('');
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) onExpired();
      else setError(e instanceof Error ? e.message : 'Nu am putut trimite răspunsul.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="rounded-lg border border-gray-200 bg-card p-4">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span
          className="rounded-full px-2 py-0.5 font-medium text-fixed-white"
          style={{ backgroundColor: PLATFORM_COLORS[post.platform] }}
        >
          {PLATFORM_LABELS[post.platform]}
        </span>
        <span className="text-gray-500">{FORMAT_LABELS[post.format]}</span>
        <span className="text-gray-500">
          ·{' '}
          {new Date(post.publishAt).toLocaleTimeString('ro-RO', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
        <span className={`ml-auto rounded-full px-2 py-0.5 ${APPROVAL_TONES[post.approvalStatus]}`}>
          {CLIENT_APPROVAL_LABELS[post.approvalStatus]}
        </span>
      </div>
      <h3 className="mt-2 font-medium text-gray-900">{post.title}</h3>
      {post.caption && (
        <p className="mt-1.5 whitespace-pre-wrap text-sm text-gray-700">{post.caption}</p>
      )}
      {post.mediaUrl && (
        <a
          href={post.mediaUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          <Video size={14} /> Vezi video-ul <ExternalLink size={12} />
        </a>
      )}

      {mode === 'idle' ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {post.approvalStatus !== 'APPROVED' && (
            <button
              type="button"
              onClick={() => setMode('approve')}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-fixed-white hover:bg-brand-700"
            >
              <CheckCircle2 size={14} /> Aprob
            </button>
          )}
          <button
            type="button"
            onClick={() => setMode('changes')}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cer modificări
          </button>
        </div>
      ) : (
        <div className="mt-3 space-y-2 rounded-md bg-gray-50 p-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Numele tău"
            className="w-full rounded-md border border-gray-300 bg-card px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none"
          />
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            placeholder={mode === 'changes' ? 'Ce vrei să schimbăm?' : 'Un comentariu (opțional)'}
            className="w-full rounded-md border border-gray-300 bg-card px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none"
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void send()}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-fixed-white hover:bg-brand-700 disabled:opacity-50"
            >
              {mode === 'approve' ? 'Trimite aprobarea' : 'Trimite cererea'}
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('idle');
                setError(null);
              }}
              className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            >
              Renunță
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
